import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/SEO";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Check, X, CaretRight, ArrowsLeftRight, ChartLineUp, MagnifyingGlass, Plus, DownloadSimple
} from "@phosphor-icons/react";

const formatNumber = (num) => {
  if (num >= 10000000) return (num / 10000000).toFixed(1) + "Cr";
  if (num >= 100000) return (num / 100000).toFixed(1) + "L";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toLocaleString("en-IN");
};

export default function AdminUsersPage() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [approvalData, setApprovalData] = useState({ chapter_id: "", role: "member" });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newUser, setNewUser] = useState({ full_name: "", business_name: "", business_category: "", email: "", phone: "", password: "", chapter_id: "", role: "member" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, chaptersRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/users-with-metrics${filter !== "all" ? `?status_filter=${filter}` : ""}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/chapters`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`)
      ]);
      setUsers(usersRes.data);
      setChapters(chaptersRes.data);
      setCategories(catRes.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  usePolling(fetchData, 10000);

  const handleApprove = async (user) => {
    if (!approvalData.chapter_id) { toast.error("Please select a chapter"); return; }
    try {
      await axios.put(`${API}/admin/users/${user.id}/approve`, {
        status: "approved", chapter_id: approvalData.chapter_id, role: approvalData.role
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User approved"); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to approve"); }
  };

  const handleReject = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`, { status: "rejected" }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User rejected"); fetchData();
    } catch (error) { toast.error("Failed to reject"); }
  };

  const handleCreateUser = async () => {
    if (!newUser.full_name || !newUser.email || !newUser.password || !newUser.chapter_id || !newUser.business_category) {
      toast.error("Please fill all required fields"); return;
    }
    try {
      await axios.post(`${API}/admin/users/create`, newUser, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${newUser.full_name} added successfully!`);
      setAddDialogOpen(false);
      setNewUser({ full_name: "", business_name: "", business_category: "", email: "", phone: "", password: "", chapter_id: "", role: "member" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const filteredUsers = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.business_name?.toLowerCase().includes(q);
  });

  const statusCounts = {
    all: users.length,
    approved: users.filter(u => u.status === "approved").length,
    pending: users.filter(u => u.status === "pending").length,
    rejected: users.filter(u => u.status === "rejected").length,
  };

  return (
    <>
      <SEO title="User Management" description="Manage BCH user registrations, approvals, and membership status." path="/admin/users" noIndex />
      <div className="space-y-6 animate-fadeInUp" data-testid="admin-users-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-primary">User Management</h1>
            <p className="text-secondary text-sm mt-1">{users.length} total members</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-56">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <Input placeholder="Search members..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-10 bg-surface border-[var(--border-color)] text-primary" data-testid="user-search-input" />
            </div>
            <Button onClick={() => setAddDialogOpen(true)} className="bg-bch-teal hover:bg-bch-teal-dark rounded-xl shadow-lg shadow-bch-teal/20" data-testid="add-user-btn">
              <Plus className="w-5 h-5 mr-2" /> Add User
            </Button>
            <Button onClick={() => window.open(`${API}/admin/export/members`, "_blank")} variant="outline" className="border-bch-gold/30 text-bch-gold hover:bg-bch-gold/5 rounded-xl" data-testid="export-members-btn">
              <DownloadSimple className="w-4 h-4 mr-2" /> Export
            </Button>
          </div>
        </div>

        {/* Add User Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="bg-card border-[var(--border-color)] max-w-lg">
            <DialogHeader><DialogTitle className="text-primary text-xl">Add New Member</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-secondary">Full Name *</Label>
                  <Input value={newUser.full_name} onChange={e => setNewUser(p => ({ ...p, full_name: e.target.value }))}
                    className="bg-surface border-[var(--border-color)] text-primary" placeholder="John Doe" data-testid="new-user-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-secondary">Email *</Label>
                  <Input type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
                    className="bg-surface border-[var(--border-color)] text-primary" placeholder="john@example.com" data-testid="new-user-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-secondary">Business Name</Label>
                  <Input value={newUser.business_name} onChange={e => setNewUser(p => ({ ...p, business_name: e.target.value }))}
                    className="bg-surface border-[var(--border-color)] text-primary" placeholder="Doe Enterprises" />
                </div>
                <div className="space-y-2">
                  <Label className="text-secondary">Category *</Label>
                  <Select value={newUser.business_category} onValueChange={v => setNewUser(p => ({ ...p, business_category: v }))}>
                    <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent className="bg-card border-[var(--border-color)]">
                      {categories.map(c => (<SelectItem key={c.id} value={c.name} className="text-primary">{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-secondary">Phone</Label>
                  <Input value={newUser.phone} onChange={e => setNewUser(p => ({ ...p, phone: e.target.value }))}
                    className="bg-surface border-[var(--border-color)] text-primary" placeholder="+91 98765 43210" />
                </div>
                <div className="space-y-2">
                  <Label className="text-secondary">Password *</Label>
                  <Input type="password" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))}
                    className="bg-surface border-[var(--border-color)] text-primary" placeholder="Min 6 characters" data-testid="new-user-password" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-secondary">Chapter *</Label>
                  <Select value={newUser.chapter_id} onValueChange={v => setNewUser(p => ({ ...p, chapter_id: v }))}>
                    <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary"><SelectValue placeholder="Assign chapter" /></SelectTrigger>
                    <SelectContent className="bg-card border-[var(--border-color)]">
                      {chapters.map(ch => (<SelectItem key={ch.id} value={ch.id} className="text-primary">{ch.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-secondary">Role</Label>
                  <Select value={newUser.role} onValueChange={v => setNewUser(p => ({ ...p, role: v }))}>
                    <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-[var(--border-color)]">
                      <SelectItem value="member" className="text-primary">Member</SelectItem>
                      <SelectItem value="president" className="text-primary">President</SelectItem>
                      <SelectItem value="vice_president" className="text-primary">Vice President</SelectItem>
                      <SelectItem value="secretary" className="text-primary">Secretary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setAddDialogOpen(false)} className="rounded-xl">Cancel</Button>
              <Button onClick={handleCreateUser} className="bg-bch-teal hover:bg-bch-teal-dark rounded-xl" data-testid="create-user-submit">Add Member</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="flex gap-2 border-b border-[var(--border-color)] pb-0">
          {[{ key: "all", label: "All" }, { key: "approved", label: "Active" }, { key: "pending", label: "Pending" }, { key: "rejected", label: "Rejected" }].map(tab => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${filter === tab.key ? "border-bch-teal text-bch-teal" : "border-transparent text-secondary hover:text-primary"}`}
              data-testid={`filter-tab-${tab.key}`}>
              {tab.label}
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${filter === tab.key ? "bg-bch-teal/15 text-bch-teal" : "bg-surface text-muted"}`}>{statusCounts[tab.key]}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" /></div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-20 bg-card border border-[var(--border-color)] rounded-2xl">
            <Users className="w-16 h-16 text-muted/30 mx-auto mb-4" weight="duotone" />
            <p className="text-secondary">No users found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="users-grid">
            {filteredUsers.map(user => (
              <div key={user.id} className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden transition-all duration-200 hover:border-bch-teal/30" data-testid={`user-card-${user.id}`}>
                <div className="p-5 flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center shrink-0">
                      <span className="text-white font-bold text-lg">{user.full_name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-primary font-semibold text-base truncate">{user.full_name}</h3>
                        <span className={`px-2 py-0.5 rounded-lg text-[10px] font-semibold uppercase tracking-wider ${
                          user.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                          user.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600"
                        }`}>{user.status}</span>
                      </div>
                      <p className="text-muted text-xs truncate">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-bch-gold text-xs font-medium">{user.business_category || "—"}</span>
                        {user.chapter_name && (<><span className="text-muted text-xs">·</span><span className="text-secondary text-xs">{user.chapter_name}</span></>)}
                        {user.role && user.role !== "member" && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-md font-medium uppercase">{user.role.replace("_", " ")}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)} className="text-secondary hover:text-primary p-1 shrink-0" data-testid={`expand-user-${user.id}`}>
                    <CaretRight className={`w-5 h-5 transition-transform duration-200 ${expandedUser === user.id ? "rotate-90" : ""}`} />
                  </button>
                </div>

                {user.status === "approved" && (
                  <div className="px-5 pb-4 grid grid-cols-4 gap-3">
                    <MetricPill label="Refs Given" value={user.referrals_given || 0} />
                    <MetricPill label="Refs Received" value={user.referrals_received || 0} color="gold" />
                    <MetricPill label="Biz Given" value={`₹${formatNumber(user.business_given || 0)}`} />
                    <MetricPill label="Biz Received" value={`₹${formatNumber(user.business_received || 0)}`} color="gold" />
                  </div>
                )}

                {expandedUser === user.id && (
                  <div className="border-t border-[var(--border-color)] bg-surface/50 px-5 py-4 space-y-4 animate-fadeInUp">
                    {user.status === "approved" && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div>
                          <p className="text-muted text-[11px] uppercase tracking-wider mb-1">Attendance</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-[var(--border-color)] rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-sky-400 to-blue-500 rounded-full" style={{ width: `${Math.min(user.attendance_percentage || 0, 100)}%` }} />
                            </div>
                            <span className="text-primary font-mono text-sm font-semibold">{user.attendance_percentage || 0}%</span>
                          </div>
                          <p className="text-muted text-[11px] mt-1">{user.attended_meetings || 0}/{user.total_meetings || 0} meetings</p>
                        </div>
                        <div>
                          <p className="text-muted text-[11px] uppercase tracking-wider mb-1">Business</p>
                          <p className="text-primary font-medium">{user.business_name || "—"}</p>
                          <p className="text-muted text-xs">{user.phone || "—"}</p>
                        </div>
                        <div>
                          <p className="text-muted text-[11px] uppercase tracking-wider mb-1">Membership</p>
                          {user.membership_expiry ? (
                            <p className={`text-sm font-medium ${new Date(user.membership_expiry) < new Date() ? "text-red-500" : "text-emerald-600"}`}>
                              {new Date(user.membership_expiry).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </p>
                          ) : (<p className="text-muted text-sm">No expiry set</p>)}
                        </div>
                      </div>
                    )}
                    {user.status === "pending" && (
                      <div className="space-y-3">
                        <p className="text-secondary text-sm font-medium">Approve this member:</p>
                        <div className="grid grid-cols-2 gap-3">
                          <Select value={approvalData.chapter_id} onValueChange={(v) => setApprovalData(p => ({ ...p, chapter_id: v }))}>
                            <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary text-sm"><SelectValue placeholder="Assign chapter" /></SelectTrigger>
                            <SelectContent className="bg-card border-[var(--border-color)]">
                              {chapters.map(ch => (<SelectItem key={ch.id} value={ch.id} className="text-primary">{ch.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                          <Select value={approvalData.role} onValueChange={(v) => setApprovalData(p => ({ ...p, role: v }))}>
                            <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-card border-[var(--border-color)]">
                              <SelectItem value="member" className="text-primary">Member</SelectItem>
                              <SelectItem value="president" className="text-primary">President</SelectItem>
                              <SelectItem value="vice_president" className="text-primary">Vice President</SelectItem>
                              <SelectItem value="secretary" className="text-primary">Secretary</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => handleApprove(user)} className="bg-bch-teal hover:bg-bch-teal-dark text-sm" size="sm"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                          <Button onClick={() => handleReject(user.id)} variant="destructive" size="sm" className="text-sm"><X className="w-4 h-4 mr-1" /> Reject</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const MetricPill = ({ label, value, color = "teal" }) => (
  <div className="text-center">
    <p className={`font-mono text-base font-bold ${color === "gold" ? "text-amber-500" : "text-bch-teal"}`}>{value}</p>
    <p className="text-muted text-[10px] leading-tight mt-0.5">{label}</p>
  </div>
);
